import { Link } from "react-router-dom";
import { ShieldCheck, Sun, Users, MapPin } from "lucide-react";
import { Seo, breadcrumbJsonLd } from "../../components/Seo";
import { CallCta } from "../../components/CallCta";
import { Testimonials } from "../../components/Testimonials";
import { SunLogo } from "../../components/SunLogo";

const VALUES_FR = [
  { icon: Sun, title: "Vigilance", text: "Comme le soleil sur la prairie, notre veille ne s'éteint jamais. Nous protégeons les gens et les biens jour et nuit." },
  { icon: Users, title: "Communauté", text: "Nous embauchons localement, formons rigoureusement et investissons dans les communautés que nous desservons — y compris les Premières Nations." },
  { icon: ShieldCheck, title: "Professionnalisme", text: "Gardiens licenciés, quarts supervisés, patrouilles vérifiées par GPS et rapports documentés sur chaque site." },
  { icon: MapPin, title: "Portée", text: "Un seul partenaire de sécurité pour le Québec et le Manitoba — grands centres, régions et communautés nordiques." },
];

export default function AboutFr() {
  return (
    <div data-testid="about-fr-page">
      <Seo
        lang="fr-CA"
        title="À Propos de 1st Nations Security | Entreprise Autochtone"
        description="Découvrez 1st Nations Security — une entreprise de gardiennage détenue par des Autochtones, au service du Québec et du Manitoba avec des gardiens licenciés, 24/7."
        path="/francais/a-propos/"
        jsonLd={[breadcrumbJsonLd([{ name: "Accueil", path: "/francais" }, { name: "À propos", path: "/francais/a-propos" }])]}
      />
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-500 mb-6" aria-label="Fil d'Ariane">
            <Link to="/francais" className="hover:text-[#FF9100]" data-testid="breadcrumb-fr-accueil">Accueil</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">À propos</span>
          </nav>
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6">
              <SunLogo size={56} />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#FF9100] border border-[#FF9100]/40 bg-[#FF9100]/10 rounded-full px-4 py-1.5">Détenue et exploitée par des Autochtones</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">
              À Propos de <span className="text-[#FF9100]">1st Nations Security</span>
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              1st Nations Security est une entreprise de gardiennage détenue par des Autochtones, au service du Québec et du Manitoba. Notre nom — et le soleil de notre logo — reflètent qui nous sommes : une entreprise enracinée dans les valeurs des Premières Nations de protection, de vigilance et d'attention portée à la communauté, offrant des services de sécurité modernes et professionnels.
            </p>
            <p className="text-gray-400 leading-relaxed mb-4">
              Pour nos clients québécois, travailler avec nous, c'est choisir un partenaire de sécurité qui allie des opérations professionnelles licenciées aux valeurs des communautés d'où nous venons — le respect, la vigilance et l'engagement. Votre investissement en sécurité soutient l'emploi et la formation des Autochtones dans l'industrie, sans compromis sur la qualité : gardiens licenciés, supervision de chaque quart et rapports numériques après chaque patrouille.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Nous protégeons les entreprises, chantiers de construction, installations industrielles, événements, établissements de santé, commerces et immeubles résidentiels — de Montréal à Winnipeg, de Gatineau à Thompson. Chaque mandat est appuyé par une répartition 24/7.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0A0B0E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-10">Nos Valeurs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES_FR.map((v, i) => (
              <div key={i} className="bg-[#14161C] border border-white/8 rounded-2xl p-6" data-testid={`value-fr-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#FF9100]/10 border border-[#FF9100]/25 flex items-center justify-center text-[#FF9100] mb-4">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-6">
            <Link to="/francais/services-de-securite" data-testid="about-fr-services-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">Découvrez nos services de sécurité →</Link>
            <Link to="/francais/villes" data-testid="about-fr-villes-link" className="inline-flex items-center gap-2 text-[#FF9100] font-semibold hover:gap-3 transition-all">Voyez les villes desservies →</Link>
          </div>
        </div>
      </section>

      <Testimonials fr />
      <CallCta heading="Travaillez Avec une Entreprise de Sécurité Autochtone" sub="Appelez ou écrivez-nous en tout temps — notre équipe de répartition répond 24/7." testid="about-fr-cta" />
    </div>
  );
}
